import { Component, ElementRef, Input, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IgxGridComponent, IgxNumberFilteringOperand, IgxStringFilteringOperand } from "@infragistics/igniteui-angular";
import { Utilities } from "@tylertech/shared-module";
import { IGridViewMetadata } from "@tylertech/tyler-api-types";
import { GridFilterService } from "../../../services";

@Component({
    selector: "grid-filter",
    templateUrl: "./gridFilter.tmpl.html",
    standalone: false
})
export class GridFilterComponent
{
    @Input() grid: IgxGridComponent;
    @Input() columns: any[];
    @Input() viewMetadata: IGridViewMetadata;

    @ViewChild("filterInput")
    filterInput: ElementRef<HTMLInputElement>;

    private columnFilters: Record<string, string> = {};

    constructor(private $gridFilterService: GridFilterService)
    {
        this.$gridFilterService.clearFilterObservable.pipe(takeUntilDestroyed())
            .subscribe(() => this.clearFilter());
    }

    private _lastFilterColumnField: string;

    get currentFilterColumnField(): string
    {
        if (this.grid?.excelStyleFilteringComponent?.column)
            this._lastFilterColumnField = this.grid?.excelStyleFilteringComponent?.column.field;
        return this._lastFilterColumnField;
    }

    get columnFilterText(): string
    {
        const columnField = this.currentFilterColumnField;
        if (!columnField) return null;
        this.filterInput?.nativeElement.focus();
        return this.columnFilters[columnField];
    }

    set columnFilterText(value: string)
    {
        const columnField = this.currentFilterColumnField;
        if (columnField) this.columnFilters[columnField] = value;
    }

    get filterColumnType(): string
    {
        const columnField = this.currentFilterColumnField;
        if (!columnField || !this.viewMetadata) return "text";
        switch (this.columns.find(x => x.Name === columnField)?.DataType)
        {
            case "string":
                return "text";
            default:
                return this.columns.find(x => x.Name === columnField)?.DataType;
        }
    }

    onInputKeyDown(event): void
    {
        if (event.key === "Enter" || event.key === "NumPadEnter")
        {
            event.preventDefault();
            this.applyFilter();
        }
        if (event.key === "Escape")
        {
            event.preventDefault();
            this.clearFilter();
        }
    }

    applyFilter(): void
    {
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.columnFilterText))
        {
            if (this.filterColumnType !== "number")
            {
                let condition = "contains";
                let useFilter = this.columnFilterText;

                if (this.columnFilterText.startsWith("*"))
                {
                    condition = "endsWith";
                    useFilter = this.columnFilterText.substring(1);
                }
                else if (this.columnFilterText.endsWith("*"))
                {
                    condition = "startsWith";
                    useFilter = this.columnFilterText.substring(0, this.columnFilterText.length - 1);
                }
                this.grid.filter(
                    this.currentFilterColumnField,
                    useFilter,
                    IgxStringFilteringOperand.instance().condition(condition),
                    true
                );
            }
            else
            {
                this.grid.filter(
                    this.currentFilterColumnField,
                    Number(this.columnFilterText),
                    IgxNumberFilteringOperand.instance().condition("equals")
                );
            }
        }
        this.grid.excelStyleFilteringComponent.cancel();
    }

    closeFilter(): void
    {
        this.clearFilter();
        this.grid.excelStyleFilteringComponent.cancel();
    }

    clearFilter(): void
    {
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.grid.filteringExpressionsTree.filteringOperands))
        {
            const index = this.grid.filteringExpressionsTree.filteringOperands.findIndex(x => x.fieldName === "$cellEditAdded");
            if (index > -1)
                this.grid.filteringExpressionsTree.filteringOperands.splice(index);
        }

        this.columnFilterText = null;
    }
}
